import './FloatingPanel.scss';
import cn from 'classnames';
import {
  ComponentProps,
  Dispatch,
  MouseEventHandler,
  ReactNode,
  SetStateAction,
  useEffect,
  useLayoutEffect,
  useState
} from 'react';
import { BsLayoutSidebar } from 'react-icons/bs';
import { MdFullscreenExit, MdOpenInFull, MdRemove } from 'react-icons/md';
import { IconButton } from '../IconButton';
import { Appearance, Design, Size } from '../designConstants';
import { Mixpanel, MxE } from '../../utils/mixpanelWrapper';
import { SidePanel } from './SidePanel';

const EXPAND_BUFFER_SPACE = 32;

const FLOATING_PANEL_TOOLTIP_CLASS = 'floating-panel-tooltip';

export const FloatingPanelActionButton = (props: ComponentProps<typeof IconButton>) => (
  <IconButton appearance={Appearance.EMPTY} size={Size.SM} design={Design.NEUTRAL} {...props} />
);

// Browser origin (0,0) is top-left: negative-x = left, negative-y = up,
// positive-x = right, positive-y = down.
type DirectionType = 'negative' | 'positive';
interface ResizeDirection {
  x: DirectionType | undefined;
  y: DirectionType | undefined;
}
interface ResizeHandle {
  name: string;
  direction: ResizeDirection;
}
type DragActionType = 'resize' | 'reposition';
interface DragAction {
  type: DragActionType;
  direction?: ResizeDirection;
}

const resizeHandleDirections: ResizeHandle[] = [
  { name: 'top-left-handle', direction: { x: 'negative', y: 'negative' } },
  { name: 'top-handle', direction: { x: undefined, y: 'negative' } },
  { name: 'top-right-handle', direction: { x: 'positive', y: 'negative' } },
  { name: 'right-handle', direction: { x: 'positive', y: undefined } },
  { name: 'bottom-right-handle', direction: { x: 'positive', y: 'positive' } },
  { name: 'bottom-handle', direction: { x: undefined, y: 'positive' } },
  { name: 'bottom-left-handle', direction: { x: 'negative', y: 'positive' } },
  { name: 'left-handle', direction: { x: 'negative', y: undefined } }
];

interface InitialPosition {
  xDist: number;
  xDirection: 'left' | 'right';
  yDist: number;
  yDirection: 'top' | 'bottom';
}

interface FloatingPanelProps {
  id: string;
  leftActions?: ReactNode;
  rightActions?: ReactNode;
  body: ReactNode;
  footer?: ReactNode;
  sidePanelTitle?: ReactNode;
  sidePanelRightActions?: ReactNode;
  sidePanelBody?: ReactNode;
  sidePanelFooter?: ReactNode;
  show: boolean;
  onHide: () => void;
  showSidePanel: boolean;
  setShowSidePanel: Dispatch<SetStateAction<boolean>>;
  minWidth?: number;
  maxWidth?: number;
  initialWidth: number;
  minHeight?: number;
  maxHeight?: number;
  initialHeight: number;
  initialPosition: InitialPosition;
}

export const FloatingPanel = ({
  id,
  leftActions,
  rightActions,
  body,
  footer,
  sidePanelTitle,
  sidePanelRightActions,
  sidePanelBody,
  sidePanelFooter,
  show,
  showSidePanel,
  setShowSidePanel,
  onHide,
  minWidth = -Infinity,
  maxWidth: maxWidthProp = Infinity,
  initialWidth,
  minHeight = -Infinity,
  maxHeight: maxHeightProp = Infinity,
  initialHeight,
  initialPosition
}: FloatingPanelProps) => {
  const [innerWidth, setInnerWidth] = useState(window.innerWidth);
  const [innerHeight, setInnerHeight] = useState(window.innerHeight);
  const [width, setWidth] = useState(initialWidth);
  const [height, setHeight] = useState(initialHeight);
  // (0, 0) is the top-left corner of the workable area of the page, discounting navbar + header.
  const [x, setX] = useState(0);
  const [y, setY] = useState(0);
  const [dragAction, setDragAction] = useState<DragAction | undefined>();

  const [navbarWidth, setNavbarWidth] = useState(0);
  const [headerHeight, setHeaderHeight] = useState(0);

  useLayoutEffect(() => {
    const navbar = document.querySelector<HTMLElement>('.bpm-nav');
    const header = document.querySelector<HTMLElement>('.bpm-page > .header');
    setNavbarWidth(navbar?.offsetWidth ?? 0);
    setHeaderHeight(header?.offsetHeight ?? 0);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setInnerWidth(window.innerWidth);
      setInnerHeight(window.innerHeight);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const rightEdgeX = innerWidth - navbarWidth;
  const bottomEdgeY = innerHeight - headerHeight;
  const maxX = rightEdgeX - minWidth;
  const maxY = bottomEdgeY - minHeight;
  const maxWidth = Math.min(rightEdgeX, maxWidthProp);
  const maxHeight = Math.min(bottomEdgeY, maxHeightProp);

  const { xDist, xDirection, yDist, yDirection } = initialPosition;
  const computedInitialX = xDirection === 'left' ? xDist : rightEdgeX - xDist - initialWidth;
  const computedInitialY = yDirection === 'top' ? yDist : bottomEdgeY - yDist - initialHeight;
  const initialX = computedInitialX < 0 || computedInitialX > maxX ? 0 : computedInitialX;
  const initialY = computedInitialY < 0 || computedInitialY > maxY ? 0 : computedInitialY;

  const resetState = () => {
    setShowSidePanel(false);
    setWidth(initialWidth);
    setHeight(initialHeight);
    setX(initialX);
    setY(initialY);
    setDragAction(undefined);
  };

  // When floating panel is hidden, reset sizing + positioning to initial.
  useEffect(() => {
    if (!show) {
      resetState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, initialX, initialY]);

  const isModified =
    width !== initialWidth || height !== initialHeight || x !== initialX || y !== initialY;

  const handleDrag: MouseEventHandler<HTMLDivElement> = e => {
    if (!dragAction) return;

    const { movementX, movementY } = e;
    const { type, direction } = dragAction;
    const { x: xDir, y: yDir } = direction ?? {};

    if (type === 'reposition') {
      const newX = x + movementX;
      const newY = y + movementY;

      // Shrink if new x position would push off the right edge of screen.
      if (newX + width > rightEdgeX && rightEdgeX - newX > minWidth) {
        setWidth(rightEdgeX - newX);
      }

      // Shrink if new y position would push off the bottom edge of screen.
      if (newY + height > bottomEdgeY && bottomEdgeY - newY > minHeight) {
        setHeight(bottomEdgeY - newY);
      }

      // Move x/y to new position as long as they don't exit the working area.
      if (newX >= 0 && newX <= maxX) {
        setX(current => current + movementX);
      }
      if (newY >= 0 && newY <= maxY) {
        setY(current => current + movementY);
      }
    } else if (type === 'resize') {
      if (xDir === 'negative') {
        if (width - movementX >= minWidth) {
          setX(current => current + movementX);
          setWidth(current => current - movementX);
        }
      } else if (xDir === 'positive') {
        if (width + movementX >= minWidth) {
          setWidth(current => current + movementX);
        }
      }

      if (yDir === 'negative') {
        if (height - movementY >= minHeight) {
          setY(current => current + movementY);
          setHeight(current => current - movementY);
        }
      } else if (yDir === 'positive') {
        if (height + movementY >= minHeight) {
          setHeight(current => current + movementY);
        }
      }
    }
  };

  const endDrag = () => setDragAction(undefined);

  const openSidePanel = () => {
    setShowSidePanel(true);
    Mixpanel.track(MxE.FloatingPanelOpenSidepanel, { widget: id });
  };

  const closeSidePanel = () => {
    setShowSidePanel(false);
    Mixpanel.track(MxE.FloatingPanelCloseSidepanel, { widget: id });
  };

  const minimize = () => {
    onHide();
    Mixpanel.track(MxE.FloatingPanelMinimize, { widget: id });
  };

  const resetSizePosition = () => {
    resetState();
    Mixpanel.track(MxE.FloatingPanelReset, { widget: id });
  };

  const expand = () => {
    setWidth(maxWidth - EXPAND_BUFFER_SPACE * 2);
    setHeight(maxHeight - EXPAND_BUFFER_SPACE * 2);

    const extraXSpace = innerWidth - navbarWidth - maxWidth;
    const extraYSpace = innerHeight - headerHeight - maxHeight;

    // Center the FloatingPanel at its maximum size.
    setX(extraXSpace / 2 + EXPAND_BUFFER_SPACE);
    setY(extraYSpace / 2 + EXPAND_BUFFER_SPACE);

    Mixpanel.track(MxE.FloatingPanelExpand, { widget: id });
  };

  return (
    <div
      className={cn('floating-panel-container', { 'drag-active': dragAction !== undefined })}
      role='presentation'
      style={{
        width: `calc(100vw - ${navbarWidth}px)`,
        left: `${navbarWidth}px`,
        height: `calc(100vh - ${headerHeight}px)`,
        top: `${headerHeight}px`
      }}
      onMouseMove={handleDrag}
      onMouseUp={endDrag}
      onMouseLeave={endDrag}
    >
      <div
        id={id}
        className={cn('floating-panel', { open: show, closed: !show })}
        style={{ width, height, translate: `${x}px ${y}px` }}
      >
        <SidePanel
          title={sidePanelTitle}
          rightActions={sidePanelRightActions}
          body={sidePanelBody}
          footer={sidePanelFooter}
          show={showSidePanel}
          onHide={closeSidePanel}
        />
        <div
          className='main-panel'
          role='presentation'
          onClick={showSidePanel ? closeSidePanel : undefined}
        >
          <div
            className='floating-panel-header'
            role='presentation'
            onMouseDown={() => {
              setDragAction({ type: 'reposition' });
              Mixpanel.track(MxE.FloatingPanelDragReposition, { widget: id });
            }}
          >
            <div className='floating-panel-left-actions'>
              {sidePanelBody && (
                <FloatingPanelActionButton
                  onClick={openSidePanel}
                  tooltip={{ text: 'Open side panel', class: FLOATING_PANEL_TOOLTIP_CLASS }}
                >
                  <BsLayoutSidebar />
                </FloatingPanelActionButton>
              )}
              {leftActions}
            </div>
            <div className='floating-panel-right-actions'>
              {rightActions}
              <FloatingPanelActionButton
                onClick={expand}
                tooltip={{ text: 'Expand', class: FLOATING_PANEL_TOOLTIP_CLASS }}
              >
                <MdOpenInFull />
              </FloatingPanelActionButton>
              {isModified && (
                <FloatingPanelActionButton
                  onClick={resetSizePosition}
                  tooltip={{ text: 'Reset', class: FLOATING_PANEL_TOOLTIP_CLASS }}
                >
                  <MdFullscreenExit />
                </FloatingPanelActionButton>
              )}
              <FloatingPanelActionButton
                onClick={minimize}
                tooltip={{ text: 'Minimize', class: FLOATING_PANEL_TOOLTIP_CLASS }}
              >
                <MdRemove />
              </FloatingPanelActionButton>
            </div>
          </div>
          <div className='floating-panel-body'>{body}</div>
          {footer && <div className='floating-panel-footer'>{footer}</div>}
        </div>
        <div className='handles-container'>
          {resizeHandleDirections.map(({ name, direction }) => (
            <div
              key={name}
              className={cn('handle', name)}
              role='presentation'
              onMouseDown={() => {
                setDragAction({ type: 'resize', direction });
                Mixpanel.track(MxE.FloatingPanelDragResize, { widget: id });
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
